import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../utils/api'
import { errorMessage } from '../core/errors'

export type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error'

/**
 * Eén manier van versturen voor elk formulier: laadstatus, foutmelding,
 * en nooit twee keer tegelijk (dubbelklik-bescherming).
 *
 * Bewust GEEN AbortController: een schrijfactie die al bij de server is, kun je niet
 * terugdraaien door de fetch af te breken. Wegnavigeren laat het verzoek dus afmaken;
 * dubbel plaatsen voorkom je met de idempotente `requestId`, niet met annuleren.
 *
 * - `submit` is stabiel, ook als de aanroeper een inline-functie meegeeft (de laatste
 *   `action` staat in een ref). Zo tekenen memo-kinderen niet opnieuw.
 * - Na unmount wordt er geen state meer gezet; de teruggegeven waarde komt wel door,
 *   zodat de aanroeper zelf kan beslissen (bijv. de mand legen na een geplaatste bestelling).
 */
export function useSubmit<TArgs extends unknown[], TResult>(action: (...args: TArgs) => Promise<TResult>) {
  const [status, setStatus] = useState<SubmitStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  /** De foutcode van de server (`mollie_uncertain`, `payment_changed`, …), voor gedrag per code. */
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [result, setResult] = useState<TResult | null>(null)
  const busy = useRef(false)
  const mounted = useRef(true)
  const actionRef = useRef(action)
  actionRef.current = action

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const submit = useCallback(async (...args: TArgs): Promise<TResult | null> => {
    if (busy.current) return null
    busy.current = true
    setStatus('submitting')
    setError(null)
    setErrorCode(undefined)
    setFieldErrors({})
    try {
      const value = await actionRef.current(...args)
      if (mounted.current) {
        setResult(value)
        setStatus('success')
      }
      return value
    } catch (err) {
      if (mounted.current) {
        setError(errorMessage(err) ?? 'Er ging iets mis. Probeer het zo nog eens.')
        setErrorCode(err instanceof ApiError ? err.code : undefined)
        setFieldErrors(err instanceof ApiError && err.fields ? err.fields : {})
        setStatus('error')
      }
      return null
    } finally {
      busy.current = false
    }
  }, [])

  const reset = useCallback(() => {
    setStatus('idle')
    setError(null)
    setErrorCode(undefined)
    setFieldErrors({})
    setResult(null)
  }, [])

  return {
    submit,
    reset,
    status,
    error,
    errorCode,
    fieldErrors,
    result,
    submitting: status === 'submitting',
    succeeded: status === 'success',
  }
}
